/**
 * Pure helpers for catalogue lot prev/next and current-lot resolution.
 * Kept free of React so navigation indexing can be unit-tested.
 */

export type SaleLotNavItem = {
  animalId?: number | null;
  lotNo: string;
  lotAnimalId?: number | null;
  lotAnimalName?: string | null;
  animalName?: string | null;
  askingPrice?: number | null;
  salePrice?: number | null;
  askingPriceDesc?: string | null;
  salePriceDesc?: string | null;
  vendorsId?: number | null;
  vendorsName?: string | null;
  currentOwnerId?: number | null;
  currentOwnerName?: string | null;
  lotNotes?: string | null;
  sale?: { saleName?: string | null } | null;
};

export type SaleLotNavTarget = {
  animalId: number;
  lotNo: string;
  lotAnimalId?: number | null;
};

/** Lots that can open an animal-detail page (have a numeric animal id). */
function buildSaleLotNavTargets(items: SaleLotNavItem[]): SaleLotNavTarget[] {
  return items
    .filter((item) => item.animalId != null)
    .map((item) => ({
      animalId: item.animalId!,
      lotNo: item.lotNo,
      lotAnimalId: item.lotAnimalId,
    }));
}

/**
 * Find the lot for the open animal-detail page by identity.
 * Do not use filtered-target indexes against the full items list — a preceding
 * no-ident lot shifts indexes and can show the wrong name/price/vendor/notes.
 */
function findCurrentSaleLot<T extends SaleLotNavItem>(
  items: T[],
  animalId: string,
  lotNo: string,
): T | null {
  return (
    items.find(
      (item) =>
        item.lotNo === lotNo &&
        item.animalId != null &&
        String(item.animalId) === animalId,
    ) ?? null
  );
}

export function resolveSaleLotNavigationState(
  items: SaleLotNavItem[],
  animalId: string,
  lotNo: string,
  totalCount: number,
) {
  const targets = buildSaleLotNavTargets(items);
  const currentIndex = targets.findIndex(
    (target) => String(target.animalId) === animalId && target.lotNo === lotNo,
  );
  const currentLot = findCurrentSaleLot(items, animalId, lotNo);

  const prev = currentIndex > 0 ? (targets[currentIndex - 1] ?? null) : null;
  const next =
    currentIndex >= 0 && currentIndex < targets.length - 1
      ? (targets[currentIndex + 1] ?? null)
      : null;

  const canNavigate = targets.length > 1;
  const positionLabel =
    currentIndex >= 0 && totalCount > 0
      ? `${currentIndex + 1} of ${totalCount}`
      : null;

  return {
    prev,
    next,
    currentLot,
    canNavigate,
    positionLabel,
    saleName: currentLot?.sale?.saleName ?? items[0]?.sale?.saleName ?? null,
  };
}
